import { Box, Flex, useToast } from "@chakra-ui/react";
import SidebarWithHeader from "components/SidebarWithHeader";
import AdForm from "components/campaignForms/AdForm";
import AdSetForm from "components/campaignForms/AdSetForms/AdSetForm";
import CampaignForm from "components/campaignForms/CampaignForm";
import Footer from "components/campaignForms/layouts/Footer";
import Header from "components/campaignForms/layouts/Header";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useGetAdspressoAdDataByCampaignIdQuery,
  useGetAdspressoCreativeByCampaignIdQuery,
} from "store/adspresso/adspressoAdApi";
import {
  useGetAdspressoCampaignByIdQuery,
  usePublishAdspressoCampaignMutation,
} from "store/adspresso/adspressoCampaignApi";

function AdspressoCampaignPageForClient() {
  const toast = useToast();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const stepFromUrl = Number(searchParams.get("step"));
  const initialStep =
    Number.isInteger(stepFromUrl) && !Number.isNaN(stepFromUrl)
      ? stepFromUrl
      : 0;

  const [step, setStep] = useState(initialStep);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [adsetsData, setAdsetsData] = useState(null);

  const {
    data: campaignData,
    isLoading: isLoadingCampaign,
    refetch,
  } = useGetAdspressoCampaignByIdQuery(id);

  const { data: AdspressoCreatives } = useGetAdspressoCreativeByCampaignIdQuery(
    id,
    { skip: step !== 2 }
  );
  const { data: adData } = useGetAdspressoAdDataByCampaignIdQuery(id, {
    skip: step !== 2,
  });

  const [publishAdspressoCampaign, { isLoading: isPublishing, isSuccess, isError }] =
    usePublishAdspressoCampaignMutation();

  // Keep URL step in sync whenever `step` changes (preserve other query params)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("step", String(step));
    setSearchParams(params, { replace: true });
  }, [step]);

  useEffect(() => {
    if (step === 2) {
      handleRefresh();
    }
  }, [step]);

  const handleRefresh = useCallback(() => {
    setShouldRefresh(true);
  }, []);

  const handleRefreshComplete = () => {
    setShouldRefresh(false);
  };

  useEffect(() => {
    if (campaignData?.tree && Object.keys(campaignData?.tree)) {
      navigate(`/launchpad/`);
    }
  }, [campaignData]);

  const handleAdsetsData = (data) => {
    setAdsetsData(data);
  };

  const handlePublish = async () => {
    await publishAdspressoCampaign(id)
      .unwrap()
      .then((response) => {
        // navigate to publish-tracking
        setTimeout(() => {
          navigate(`/publish-tracking/${id}`);
        }, 1000);
      });
  };

  useEffect(() => {
    if (isSuccess) {
      toast({
        position: "top-right",
        title: "Campaign published",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      toast({
        position: "top-right",
        title: "Error publishing campaign",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [isError]);

  const handleCampaignSuccess = () => {
    refetch();
  };

  const forms = [
    <CampaignForm
      key="campaign"
      isLoadingCampaign={isLoadingCampaign}
      campaignData={campaignData}
      adsetsData={adsetsData}
      onSuccess={handleCampaignSuccess}
    />,
    <AdSetForm
      key="adset"
      campaignData={campaignData}
      onAdsetsData={handleAdsetsData}
    />,
    <AdForm
      key="ad"
      campaignData={campaignData}
      onRefresh={shouldRefresh}
      onRefreshComplete={handleRefreshComplete}
    />,
  ];

  const handleNext = () => {
    if (step < forms.length - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  return (
    <SidebarWithHeader noPadding={true}>
      <Flex
        height="calc(100vh - 80px)"
        direction={{ base: "column", md: "row" }}
      >
        <Flex flexDirection="column" flex="1">
          {/* Header */}
          <Header
            step={step}
            setStep={setStep}
            campaignData={campaignData}
            adsetsData={adsetsData}
          />

          {/* Main Content Area */}
          <Box flex="1" overflowY="auto">
            {forms[step]}  {/* Display the form based on the current step */}
          </Box>

          {/* Footer */}
          <Footer
            campaignData={campaignData}
            step={step}
            handleBack={handleBack}
            handleNext={handleNext}
            adsetsData={adsetsData}
            forms={forms}
            canPublish={AdspressoCreatives?.data?.length > 0}
            onPublishCampaign={() => handlePublish()}
            isPublishing={isPublishing}
            handleCloseForm={() => navigate("/launchpad/")}
          />
        </Flex>
      </Flex>
    </SidebarWithHeader>
  );
}

export default AdspressoCampaignPageForClient;
